import React, { useEffect, useState, useCallback } from 'react';
import { Livraison, Recuperation } from '../types';
import { livraisonAPI, recuperationAPI } from '../services/api';
import { speak, buildMissionText, MESSAGE_VOIX_ABSENTE } from '../utils/tts';
import { vibrateTap } from '../utils/haptic';
import { classerErreur, ErreurMetier, ErreurReseau } from '../utils/erreursReseau';
import { subscribeToNotifications } from '../utils/notifications';
import InvitePermissionNotifications from '../components/InvitePermissionNotifications';
import {destinationMission, libelleDestination} from '../utils/destinationMission';

interface Props {
    prenom: string;
    telephone: string;
    onMissionSelect: (mission: Livraison | Recuperation, type: 'livraison' | 'recuperation') => void;
    onLogout: () => void;
}

const AccueilScreen: React.FC<Props> = ({ prenom, telephone, onMissionSelect, onLogout }) => {
    const [livraisons, setLivraisons]       = useState<Livraison[]>([]);
    const [recuperations, setRecuperations] = useState<Recuperation[]>([]);
    const [loading, setLoading]             = useState(true);
    const [online, setOnline]               = useState(navigator.onLine);
    /**
     * Échec du chargement. Auparavant `catch { /* offline gracieux *\/ }` avalait tout : une
     * session expirée, une mission refusée par le serveur ou une panne backend se présentaient
     * toutes comme « Aucune mission ». Le chauffeur concluait qu'il n'avait rien à faire.
     */
    const [erreur, setErreur]               = useState<ErreurReseau | null>(null);

    useEffect(() => {
        const onOnline  = () => setOnline(true);
        const onOffline = () => setOnline(false);
        window.addEventListener('online',  onOnline);
        window.addEventListener('offline', onOffline);
        return () => { window.removeEventListener('online', onOnline); window.removeEventListener('offline', onOffline); };
    }, []);

    const loadMissions = useCallback(async () => {
        try {
            setLoading(true);
            setErreur(null);
            const [lRes, rRes] = await Promise.all([
                livraisonAPI.getAll({ telephone }),
                recuperationAPI.getAll(),
            ]);
            // Le backend peut répondre 200 en signalant lui-même un échec métier.
            if (lRes.success === false || rRes.success === false) {
                throw new ErreurMetier(lRes.message || rRes.message || 'Chargement refusé par le serveur.');
            }
            // Afficher uniquement les missions NON terminées
            const DONE = ['LIVRE','TERMINEE','REFUSE','RECUPEREE'];
            const activeLivr = (lRes.data || []).filter(l =>
                l.statutWorkflow && !DONE.includes(l.statutWorkflow)
            );
            const activeRecup = (rRes.data || []).filter(r =>
                r.statut && !DONE.includes(String(r.statut))
            );
            setLivraisons(activeLivr);
            setRecuperations(activeRecup as any);
        } catch (e: any) {
            setErreur(classerErreur(e));
        } finally { setLoading(false); }
    }, [telephone]);

    useEffect(() => { loadMissions(); }, [loadMissions]);

    /**
     * Flux temps réel (PWA-01).
     *
     * Ce branchement corrige un défaut de fond : `subscribeToNotifications` n'était appelé que
     * depuis `Dashboard`, composant mort supprime le 2026-09-20. Le flux SSE modernisé par SEC-07
     * n'était donc ouvert nulle part, et cet écran chargeait ses missions une seule fois, sans
     * même une relance périodique — une livraison affectée en cours de journée restait invisible
     * jusqu'à ce que le chauffeur recharge la page de lui-même.
     *
     * L'ouverture ne dépend PAS de la permission de notification, et ne doit jamais en dépendre :
     * refuser les notifications système ne doit pas couper le temps réel. C'est ce qu'établit la
     * mutation D du chantier.
     *
     * Le flux ne produit aucune notification système : il rafraîchit les missions. La
     * notification, elle, vient du Web Push, qui seul atteint un téléphone verrouillé.
     */
    useEffect(() => {
        if (!telephone) {
            return undefined;
        }
        const actif = { current: true };
        const fermer = subscribeToNotifications(telephone, () => { loadMissions(); }, actif);
        return () => { actif.current = false; fermer(); };
    }, [telephone, loadMissions]);

    // Regrouper toutes les missions actives
    const allMissions = [
        ...livraisons.map(l => ({ data: l, type: 'livraison' as const })),
        ...recuperations.map(r => ({ data: r as any, type: 'recuperation' as const })),
    ];

    const activeMission = allMissions[0] ?? null;

    const [voixAbsente, setVoixAbsente] = useState(false);

    const handleVoice = () => {
        vibrateTap();

        const texte = activeMission
            ? buildMissionText({
                  prenom,
                  conteneur: activeMission.data.conteneur || activeMission.data.numeroBL || '',
                  client:    activeMission.data.clientDestination || activeMission.data.client || '',
                  // La voix annoncait la destination finale sur un depot parc, comme l ecran.
                  lieu:      destinationMission(activeMission.data),
                  type: activeMission.type,
              })
            : `Bonjour ${prenom}. Vous n'avez pas de mission en attente pour le moment.`;

        // speak() renvoie false quand le téléphone n'a aucune voix française. Sans ce
        // retour, le bouton paraissait simplement inerte et le chauffeur n'avait aucun
        // moyen de comprendre pourquoi — ni de le corriger.
        if (!speak(texte)) setVoixAbsente(true);
    };

    const handleSelect = (data: any, type: 'livraison' | 'recuperation') => {
        vibrateTap();
        onMissionSelect(data, type);
    };

    if (loading) {
        return (
            <div style={styles.loadingScreen}>
                <div style={styles.spinner} />
                <p style={styles.loadingText}>Chargement des missions...</p>
            </div>
        );
    }

    return (
        <div style={styles.screen}>
            {/* Header */}
            <div style={styles.header}>
                {!online && (
                    <div style={styles.offlineBanner}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true" focusable="false" style={{ marginRight: 6, verticalAlign: -2 }}>
                            <path d="M2 8.82a15 15 0 0 1 20 0"/>
                            <path d="M5 12.86a10 10 0 0 1 14 0"/>
                            <path d="M8.5 16.43a5 5 0 0 1 7 0"/>
                            <path d="M2 2l20 20"/>
                        </svg>
                        Mode hors-ligne — Données locales
                    </div>
                )}
                <div style={styles.headerContent}>
                    <div>
                        <p style={styles.greet}>Bonjour 👋</p>
                        <h1 style={styles.name}>{prenom.toUpperCase()}</h1>
                    </div>
                    <button onClick={onLogout} style={styles.logoutBtn} aria-label="Déconnexion" title="Déconnexion">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" aria-hidden="true" focusable="false">
                            <path d="M12 3v9"/>
                            <path d="M18.4 6.6a8 8 0 1 1-12.8 0"/>
                        </svg>
                    </button>
                </div>
            </div>

            {/* Invitation aux notifications (PWA-01). Ne s'affiche que si la permission n'a jamais
                été tranchée : accordée, elle est inutile ; refusée, insister serait hostile. */}
            <div style={{ padding: '0 20px' }}>
                <InvitePermissionNotifications />
            </div>

            {/* Le bouton ÉCOUTER n'a de sens que s'il y a une mission à décrire — l'afficher à vide
                mettait en avant une action non actionnable au sommet de l'écran (cf. retour terrain). */}
            {allMissions.length > 0 && (
                <>
                <button onClick={handleVoice} style={styles.voiceBtn}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" aria-hidden="true" focusable="false">
                        <path d="M11 5 6 9H2v6h4l5 4z"/>
                        <path d="M15.5 8.5a5 5 0 0 1 0 7"/>
                        <path d="M18.5 6a9 9 0 0 1 0 12"/>
                    </svg>
                    <span style={styles.voiceBtnText}>ÉCOUTER MA MISSION</span>
                </button>
                {voixAbsente && (
                    <p style={styles.voixAbsente}>{MESSAGE_VOIX_ABSENTE}</p>
                )}
                </>
            )}

            {/* Missions */}
            <div style={styles.content}>
                {erreur ? (
                    /* Un échec ne doit jamais ressembler à « aucune mission » : le chauffeur
                       partirait sans savoir que des missions l'attendent peut-être. */
                    <div style={styles.noMission} role="alert">
                        <div style={{ width: 64, height: 64, borderRadius: 16, background: '#ef4444', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 34 }}>
                            ⚠️
                        </div>
                        <h2 style={styles.noMissionTitle}>Missions non chargées</h2>
                        <p style={styles.noMissionSub}>{erreur.message}</p>
                        {erreur.categorie === 'NON_AUTHENTIFIE' ? (
                            <button onClick={() => window.location.reload()} style={styles.refreshBtn}>
                                Se reconnecter
                            </button>
                        ) : (
                            <button onClick={loadMissions} style={styles.refreshBtn}>
                                Réessayer
                            </button>
                        )}
                    </div>
                ) : allMissions.length === 0 ? (
                    <div style={styles.noMission}>
                        <div style={{ width: 64, height: 64, borderRadius: 16, background: '#22c55e', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" aria-hidden="true" focusable="false">
                                <path d="M5 13l4 4L19 7"/>
                            </svg>
                        </div>
                        <h2 style={styles.noMissionTitle}>Aucune mission</h2>
                        <p style={styles.noMissionSub}>Vous n'avez pas de mission en attente.</p>
                        <button onClick={loadMissions} style={styles.refreshBtn}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true" focusable="false" style={{ marginRight: 8, verticalAlign: -3 }}>
                                <path d="M3 12a9 9 0 1 1 3 6.7"/>
                                <path d="M3 17v-5h5"/>
                            </svg>
                            Actualiser
                        </button>
                    </div>
                ) : (
                    <>
                        <p style={styles.sectionLabel}>
                            {allMissions.length} mission{allMissions.length > 1 ? 's' : ''} en cours
                        </p>
                        {allMissions.map(({ data, type }, i) => (
                            <MissionCard
                                key={data.id}
                                data={data}
                                type={type}
                                isFirst={i === 0}
                                onClick={() => handleSelect(data, type)}
                            />
                        ))}
                    </>
                )}
            </div>
        </div>
    );
};

/* ── Mission Card ── */
interface CardProps {
    data: any;
    type: 'livraison' | 'recuperation';
    isFirst: boolean;
    onClick: () => void;
}

const MissionCard: React.FC<CardProps> = ({ data, type, isFirst, onClick }) => {
    const isLivr = type === 'livraison';
    const conteneur = data.conteneur || data.numeroBL || '—';
    const client    = data.clientDestination || data.client || '—';
    // Sur un depot parc, la destination du jour est le PARC : cf. utils/destinationMission.
    const lieu        = destinationMission(data);
    const libelleLieu = libelleDestination(data);
    const statut    = data.statutWorkflow || data.status || '';

    const STATUT_LABELS: Record<string, string> = {
        ATTENTE: '⏳ En attente',
        ACCEPTER: '✅ Acceptée',
        EN_ROUTE: '🚚 En route',
        ARRIVE: '📍 Arrivé',
        EN_LIVRAISON: '📦 En livraison',
        EN_COURS: '♻️ En récupération',
    };

    return (
        <button onClick={onClick} style={{ ...styles.card, borderColor: isFirst ? '#22c55e' : '#f59e0b', borderWidth: isFirst ? 3 : 2 }}>
            {/* Badge type */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <span style={{ ...styles.typeBadge, background: isLivr ? '#f59e0b' : '#0369a1' }}>
                    {isLivr ? '📦 LIVRAISON' : '♻️ RÉCUPÉRATION'}
                </span>
                {isFirst && <span style={styles.priorityBadge}>🔴 PRIORITAIRE</span>}
            </div>

            {/* Conteneur — très grand */}
            <div style={styles.conteneurBox}>
                <p style={styles.conteneurLabel}>CONTENEUR</p>
                <p style={styles.conteneurNum}>{conteneur}</p>
            </div>

            {/* Client + Lieu */}
            <div style={styles.infoRow}>
                <span style={{ fontSize: 20 }}>👤</span>
                <span style={styles.infoText}>{client}</span>
            </div>
            <div style={styles.infoRow}>
                <span style={{ fontSize: 20 }}>📍</span>
                <span style={styles.infoText}>{libelleLieu === 'DÉPÔT PARC' ? `🅿️ DÉPÔT PARC — ${lieu}` : lieu}</span>
            </div>

            {/* Statut */}
            <div style={styles.statutRow}>
                {STATUT_LABELS[statut] || statut}
            </div>

            {/* CTA */}
            <div style={{ ...styles.ctaBtn, background: isFirst ? '#22c55e' : '#f59e0b' }}>
                <span style={styles.ctaBtnText}>
                    {isFirst ? 'VOIR MA MISSION →' : 'VOIR →'}
                </span>
            </div>
        </button>
    );
};

/* ── Styles ── */
const styles: Record<string, React.CSSProperties> = {
    screen: { minHeight: '100vh', background: '#0f172a', display: 'flex', flexDirection: 'column' },
    loadingScreen: { minHeight: '100vh', background: '#0f172a', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16 },
    spinner: { width: 48, height: 48, border: '4px solid #1e293b', borderTopColor: '#22c55e', borderRadius: '50%', animation: 'spin 0.8s linear infinite' },
    loadingText: { color: '#64748b', fontSize: 16 },
    offlineBanner: { background: '#ef4444', color: '#fff', textAlign: 'center', padding: '8px', fontSize: 13, fontWeight: 700 },
    header: { background: 'linear-gradient(135deg, #15803d, #166534)', paddingTop: 'env(safe-area-inset-top, 16px)' },
    headerContent: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px 20px' },
    greet: { color: '#bbf7d0', fontSize: 14, margin: 0 },
    name: { color: '#fff', fontSize: 26, fontWeight: 900, margin: 0, letterSpacing: 1 },
    logoutBtn: { background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: 12, width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' },
    voiceBtn: {
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12,
        background: '#7c3aed', border: 'none', margin: '16px 16px 0',
        borderRadius: 16, padding: '18px 20px', cursor: 'pointer', width: 'calc(100% - 32px)',
        boxShadow: '0 4px 20px rgba(124,58,237,0.4)',
    },
    voiceBtnText: { color: '#fff', fontSize: 18, fontWeight: 900, letterSpacing: 1 },
    voixAbsente: {
        margin: '10px 0 0', padding: '10px 12px', borderRadius: 10,
        background: '#fef3c7', color: '#92400e', fontSize: 14, lineHeight: 1.45,
    } as React.CSSProperties,
    content: { flex: 1, padding: '16px', overflowY: 'auto' },
    sectionLabel: { color: '#64748b', fontSize: 13, fontWeight: 600, marginBottom: 12, textTransform: 'uppercase', letterSpacing: 1 },
    noMission: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, paddingTop: 80 },
    noMissionTitle: { color: '#f8fafc', fontSize: 22, fontWeight: 800, margin: 0 },
    noMissionSub: { color: '#64748b', fontSize: 15, margin: 0, textAlign: 'center' },
    refreshBtn: { background: '#1e293b', border: '1px solid #334155', borderRadius: 12, color: '#94a3b8', padding: '12px 24px', fontSize: 15, fontWeight: 600, cursor: 'pointer', marginTop: 8 },
    card: {
        display: 'block', width: '100%', background: '#1e293b',
        border: '2px solid #334155', borderRadius: 20, padding: 20,
        marginBottom: 16, cursor: 'pointer', textAlign: 'left',
        boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
    },
    typeBadge: { color: '#000', fontSize: 11, fontWeight: 800, padding: '4px 10px', borderRadius: 20, letterSpacing: 0.5 },
    priorityBadge: { color: '#fca5a5', fontSize: 11, fontWeight: 700 },
    conteneurBox: { background: '#0f172a', borderRadius: 12, padding: '12px 16px', marginBottom: 14 },
    conteneurLabel: { color: '#64748b', fontSize: 10, fontWeight: 700, letterSpacing: 1, margin: 0, textTransform: 'uppercase' },
    conteneurNum: { color: '#f59e0b', fontSize: 22, fontWeight: 900, margin: 0, fontFamily: 'monospace', letterSpacing: 2 },
    infoRow: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 },
    infoText: { color: '#f1f5f9', fontSize: 16, fontWeight: 600 },
    statutRow: { color: '#94a3b8', fontSize: 13, marginBottom: 14, paddingLeft: 4 },
    ctaBtn: { borderRadius: 12, padding: '16px', textAlign: 'center' },
    ctaBtnText: { color: '#000', fontSize: 16, fontWeight: 900, letterSpacing: 1 },
};

export default AccueilScreen;
